import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import SignaturePad from "@/components/SignaturePad";
import BarcodeScanner from "@/components/BarcodeScanner";
import LiveCameraCapture from "@/components/LiveCameraCapture";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck,
  SignOut,
  Camera,
  Package as PackageIcon,
  CheckCircle,
  MapPin,
  Phone,
  ListChecks,
  ClockCounterClockwise,
  Lightning,
  ShieldWarning,
  NavigationArrow,
  Storefront,
  Coins,
  TrendUp,
  Key,
  PenNib,
  Barcode,
  UserMinus,
  SpeakerHigh,
  SpeakerSlash,
  X,
  Trophy,
  ArrowUp,
  ArrowDown,
  Minus,
  Medal,
  Crown,
  Sparkle,
  Rocket,
  Star,
  ShieldCheck,
  Lock,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";
const KG_TO_LB = 2.20462;

/**
 * Build a Google Maps directions URL for the given package.
 * Prefers exact lat/lng coordinates when available (more accurate, immune to
 * address typos). Falls back to a free-text address search.
 * On mobile, Google's universal URL automatically opens the native app.
 *
 * `leg` selects which leg of the trip:
 *   - "outbound" (default): destination = recipient (delivery leg)
 *   - "inbound": destination = sender / pickup origin (driver -> pickup leg)
 *
 * If `originCoords` (an object like {lat, lng}) is provided we set it as the
 * `origin` query param so Maps uses the driver's reported GPS even when the
 * browser hasn't already granted Maps geolocation. Otherwise Google Maps will
 * default to the user's current location automatically.
 */
function buildMapsNavUrl(pkg, leg = "outbound", originCoords = null) {
  if (!pkg) return null;
  let destination = null;
  if (leg === "inbound") {
    // Inbound leg: navigate FROM driver TO pickup origin
    if (pkg.sender_address) {
      destination = encodeURIComponent(pkg.sender_address);
    }
  } else {
    // Outbound leg: navigate to recipient
    if (pkg.recipient_lat != null && pkg.recipient_lng != null) {
      destination = `${pkg.recipient_lat},${pkg.recipient_lng}`;
    } else if (pkg.recipient_address) {
      destination = encodeURIComponent(pkg.recipient_address);
    }
  }
  if (!destination) return null;
  const parts = [
    "https://www.google.com/maps/dir/?api=1",
    `destination=${destination}`,
    "travelmode=driving",
  ];
  if (originCoords && originCoords.lat != null && originCoords.lng != null) {
    parts.push(`origin=${originCoords.lat},${originCoords.lng}`);
  }
  return parts.join("&").replace("?api=1&", "?api=1&");
}

/**
 * Try to obtain the driver's current GPS location, then open Google Maps with
 * directions to the requested leg. Falls back to opening Maps without an
 * explicit origin (Maps uses the device's current location automatically).
 */
function openMapsWithCurrentLocation(pkg, leg = "outbound") {
  const base = buildMapsNavUrl(pkg, leg);
  if (!base) return;
  const win = window.open("about:blank", "_blank");
  if (!navigator.geolocation) {
    if (win) win.location.href = base;
    return;
  }
  const TIMEOUT_MS = 4000;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const url = buildMapsNavUrl(pkg, leg, { lat: pos.coords.latitude, lng: pos.coords.longitude });
      if (win) win.location.href = url || base;
    },
    () => {
      // Permission denied / unavailable — just use destination-only URL
      if (win) win.location.href = base;
    },
    { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 30000 }
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
    in_transit: "bg-blue-50 text-blue-700 border-blue-200",
    ready_for_pickup: "bg-violet-50 text-violet-700 border-violet-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
  };
  const label = (status || "").replace(/_/g, " ");
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${map[status] || map.pending}`}>
      {label}
    </span>
  );
}

function MethodBadge({ method }) {
  const m = method || "ground";
  const styles = {
    air: "bg-sky-50 text-sky-700 border-sky-200",
    sea: "bg-cyan-50 text-cyan-700 border-cyan-200",
    ground: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${styles[m] || styles.ground}`}>
      {m}
    </span>
  );
}

function EarningCard({ label, value, currency, subtitle, hero, muted }) {
  return (
    <div
      className={`rounded-md border p-4 ${
        hero
          ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
          : muted
          ? "bg-zinc-50 border-zinc-200"
          : "bg-white border-zinc-200"
      }`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1 font-semibold ${hero ? "text-2xl text-emerald-800" : "text-lg text-zinc-900"}`} style={{ fontFamily: "Work Sans" }}>
        <span className="text-[11px] font-mono text-zinc-500 mr-1">{currency}</span>
        {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      {subtitle && <div className="text-[11px] text-zinc-500 mt-1">{subtitle}</div>}
    </div>
  );
}

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("active"); // active | history | earnings
  const [rows, setRows] = useState([]);
  const [pickups, setPickups] = useState([]); // pickup requests assigned to the driver
  const [earnings, setEarnings] = useState(null);
  // Weekly leaderboard pulse — fetched once on mount, refreshed every 5 minutes
  // so a driver who completed a delivery can see their rank update in near-real-time.
  const [leaderboard, setLeaderboard] = useState(null);
  // Achievement badges (gamification) — earned + locked, with progress toward
  // next milestone. Refreshed alongside the leaderboard.
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [delivering, setDelivering] = useState(null); // package being delivered
  const [podFile, setPodFile] = useState(null);
  const [podPreview, setPodPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  // Signature pad state — when a signature-required package is being delivered,
  // we capture the signature in a sub-dialog before allowing POD upload.
  const [signing, setSigning] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [signedByName, setSignedByName] = useState("");
  // Attempt-marker state — driver marks a failed attempt without delivering.
  const [attempting, setAttempting] = useState(null); // package being marked
  const [attemptNotes, setAttemptNotes] = useState("");
  const [attemptSubmitting, setAttemptSubmitting] = useState(false);

  // Vehicle photo refresh — drivers re-capture front + back every ~90 days so
  // courier ops always knows the on-file vehicle still matches reality.
  const [driverProfile, setDriverProfile] = useState(null);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vFront, setVFront] = useState(null);
  const [vBack, setVBack] = useState(null);
  const [vFrontGeo, setVFrontGeo] = useState({ lat: null, lng: null });
  const [vBackGeo, setVBackGeo] = useState({ lat: null, lng: null });
  const [vSubmitting, setVSubmitting] = useState(false);

  // Notification chimes + vibration on package assigned/scanned/removed.
  // Persisted "mute" toggle so drivers can silence on quiet shifts.
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("driver_chime_muted") === "1"; } catch { return false; }
  });
  const audioCtxRef = useRef(null);
  const knownIdsRef = useRef(null); // null = not initialized, Set = previous load's IDs
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem("driver_chime_muted", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  const playChime = (kind) => {
    if (muted) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* noop */ } }
      const now = ctx.currentTime;
      const tone = (freq, start, dur, gain = 0.16) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + start);
        g.gain.setValueAtTime(0, now + start);
        g.gain.linearRampToValueAtTime(gain, now + start + 0.02);
        g.gain.linearRampToValueAtTime(0, now + start + dur);
        osc.connect(g); g.connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.02);
      };
      if (kind === "assign") {
        // Bright two-tone "ding-ding" (C6 → G6)
        tone(1046.5, 0,    0.18);
        tone(1567.98, 0.16, 0.22);
      } else {
        // Soft descending "doo" (G5 → C5) on completion / removal
        tone(783.99, 0,    0.20, 0.13);
        tone(523.25, 0.18, 0.30, 0.13);
      }
    } catch { /* noop */ }
    try {
      if (navigator.vibrate) navigator.vibrate(kind === "assign" ? [200, 120, 200] : [400]);
    } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "earnings") {
        const { data } = await api.get("/driver/earnings");
        setEarnings(data);
      } else {
        // Load both packages AND pickup requests assigned to this driver in parallel
        const [pkgRes, pickRes] = await Promise.all([
          api.get("/driver/jobs", { params: { status: tab } }),
          api.get("/driver/pickups", { params: { status: tab } }).catch(() => ({ data: [] })),
        ]);
        const nextPkgs = pkgRes.data || [];
        const nextPickups = pickRes.data || [];
        // Detect package additions/removals to drive chime + vibration
        const nextIds = new Set([
          ...nextPkgs.map((p) => `pkg:${p.id}`),
          ...nextPickups.map((p) => `pickup:${p.id}`),
        ]);
        const prev = knownIdsRef.current;
        if (prev instanceof Set) {
          const added = [...nextIds].filter((x) => !prev.has(x));
          const removed = [...prev].filter((x) => !nextIds.has(x));
          if (added.length) playChime("assign");
          else if (removed.length) playChime("remove");
        }
        knownIdsRef.current = nextIds;
        setRows(nextPkgs);
        setPickups(nextPickups);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  // Driver marks an assigned pickup as picked up (Module: driver pickup workflow)
  const markPickupPickedUp = async (p) => {
    if (!confirm(`Confirm you've collected the package from ${p.pickup_address}?`)) return;
    try {
      await api.patch(`/pickups/${p.id}`, { status: "picked_up" });
      toast.success("Pickup marked collected — courier admin will convert it to a delivery shortly.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't mark pickup");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Auto-refresh the job list every 15s so the chime/vibration triggers when
  // an admin assigns or removes a package while the driver tab is open.
  useEffect(() => {
    if (tab === "earnings") return undefined;
    const t = setInterval(() => { load(); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab]);

  // Reset known-IDs whenever the tab changes so we don't trigger
  // chimes for "removals" caused purely by switching tabs.
  useEffect(() => {
    knownIdsRef.current = null;
  }, [tab]);

  // Live-location heartbeat — drives the super-admin Live Map. Sends GPS every
  // 60s while the tab is open; silently no-ops if geolocation is denied.
  useEffect(() => {
    let stopped = false;
    const beat = () => {
      if (stopped) return;
      if (!navigator.geolocation) {
        api.post("/driver/heartbeat", {}).catch(() => {});
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (stopped) return;
          api.post("/driver/heartbeat", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
        },
        () => { api.post("/driver/heartbeat", {}).catch(() => {}); },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 30_000 },
      );
    };
    beat();
    const t = setInterval(beat, 60_000);
    return () => { stopped = true; clearInterval(t); };
  }, []);

  // Pre-load earnings on mount so the "My next payout" banner is always visible
  useEffect(() => {
    api.get("/driver/earnings")
      .then(({ data }) => setEarnings(data))
      .catch(() => { /* silent — banner just won't render */ });
  }, []);

  // Pre-load leaderboard ranking + auto-refresh every 5 min while the portal is open
  useEffect(() => {
    const load = () => {
      api.get("/driver/me/leaderboard")
        .then(({ data }) => setLeaderboard(data))
        .catch(() => { /* silent — tile just won't render */ });
      api.get("/driver/me/badges")
        .then(({ data }) => setBadges(data))
        .catch(() => { /* silent — badges tile just won't render */ });
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Pre-load driver profile to know vehicle-photo freshness (drives the
  // "Update vehicle photos" reminder shown to ageing drivers)
  const loadDriverProfile = async () => {
    try {
      const { data } = await api.get("/driver/me");
      setDriverProfile(data);
    } catch { /* silent — banner just won't render */ }
  };
  useEffect(() => { loadDriverProfile(); }, []);

  // Capture browser GPS for the vehicle photo dialog (best-effort, silent fail)
  const captureGeo = (setter) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60_000 },
    );
  };

  const submitVehiclePhotos = async () => {
    if (!vFront) { toast.error("Take a live photo of the FRONT of your vehicle first."); return; }
    if (!vBack) { toast.error("Take a live photo of the BACK of your vehicle too."); return; }
    setVSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("vehicle_front", vFront.blob, "vehicle-front.jpg");
      fd.append("vehicle_back", vBack.blob, "vehicle-back.jpg");
      if (vFrontGeo.lat != null) fd.append("vehicle_front_lat", String(vFrontGeo.lat));
      if (vFrontGeo.lng != null) fd.append("vehicle_front_lng", String(vFrontGeo.lng));
      if (vBackGeo.lat != null) fd.append("vehicle_back_lat", String(vBackGeo.lat));
      if (vBackGeo.lng != null) fd.append("vehicle_back_lng", String(vBackGeo.lng));
      const { data } = await api.post("/driver/vehicle-photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDriverProfile(data);
      toast.success("Vehicle photos refreshed — you're good for another 90 days.");
      setVehicleOpen(false);
      setVFront(null); setVBack(null);
      setVFrontGeo({ lat: null, lng: null }); setVBackGeo({ lat: null, lng: null });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save photos. Try again.");
    } finally { setVSubmitting(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please pick a photo (jpg/png/webp)"); return;
    }
    setPodFile(f);
    const r = new FileReader();
    r.onload = () => setPodPreview(r.result);
    r.readAsDataURL(f);
  };

  const submitPod = async () => {
    if (!delivering || !podFile) return;
    // Module 2: signature-required packages must capture a signature BEFORE POD upload
    if (delivering.signature_required && !signatureDataUrl && !delivering.signature_image_path) {
      toast.error("A signature is required to complete this delivery.");
      setSigning(true);
      return;
    }
    setUploading(true);
    try {
      // 1) Upload signature first if we just captured one
      if (signatureDataUrl) {
        await api.post(`/packages/${delivering.id}/signature`, {
          data_url: signatureDataUrl,
          signed_by_name: signedByName,
        });
      }
      // 2) Upload POD photo + mark delivered
      const fd = new FormData();
      fd.append("file", podFile);
      await api.post(`/packages/${delivering.id}/pod`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Delivered! Proof of delivery sent to recipient.");
      setDelivering(null);
      setPodFile(null);
      setPodPreview(null);
      setSignatureDataUrl(null);
      setSignedByName("");
      setTab("history");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  // Module 3 — record a failed delivery attempt (recipient unavailable)
  const submitAttempt = async () => {
    if (!attempting) return;
    setAttemptSubmitting(true);
    try {
      const { data } = await api.post(`/packages/${attempting.id}/attempt`, {
        outcome: "recipient_unavailable",
        notes: attemptNotes.trim() || null,
      });
      const isFinal = (data.attempt_count || 0) >= 2;
      toast.success(
        isFinal
          ? "Attempt 2 recorded — package flagged for return to sender. Courier admin notified."
          : "Attempt 1 recorded — recipient notified by email."
      );
      setAttempting(null);
      setAttemptNotes("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't record attempt");
    } finally { setAttemptSubmitting(false); }
  };

  // Module 4 — scan-to-load
  const handleScan = async (decoded) => {
    setScanOpen(false);
    try {
      const { data } = await api.post("/driver/scan-load", { tracking_number: (decoded || "").toUpperCase() });
      toast.success(`Loaded ${data.tracking_number} — ${data.recipient_name}`);
      setTab("active");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load that package");
    }
  };

  const cancelPod = () => {
    setDelivering(null);
    setPodFile(null);
    setPodPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const counts = useMemo(() => ({ shown: rows.length }), [rows]);
  const isInactive = user?.driver_verification_status && user.driver_verification_status !== "verified";

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="driver-portal">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shrink-0">
              <Lightning size={18} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">ZipExpress</div>
              <div className="text-sm font-semibold truncate" style={{ fontFamily: "Work Sans" }}>
                {user?.driver_name || user?.name || "Driver"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMute}
              data-testid="driver-mute-toggle"
              className={muted ? "text-zinc-400" : "text-amber-600"}
              title={muted ? "Notifications muted — tap to unmute" : "Notifications on — tap to mute"}
            >
              {muted ? <SpeakerSlash size={16} weight="bold" /> : <SpeakerHigh size={16} weight="bold" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setScanOpen(true)} data-testid="driver-scan-btn" className="text-[#002BF6] font-semibold" title="Scan package">
              <Barcode size={17} weight="bold" className="mr-1" /> Scan
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPwdOpen(true)} data-testid="driver-change-pwd-btn" className="text-zinc-600 hidden sm:inline-flex" title="Change password">
              <Key size={16} className="mr-1" /> Password
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="driver-logout-btn" className="text-zinc-600">
              <SignOut size={16} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
        {/* Privacy indicator: location sharing transparency for drivers */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-2">
          <div className="flex items-center justify-center sm:justify-end gap-1.5 text-[10px] text-zinc-500" data-testid="driver-location-indicator">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>Live location sharing: ON · helps dispatch route closest jobs to you</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
        {isInactive && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4 flex gap-3" data-testid="driver-inactive-banner">
            <ShieldWarning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-900">Your driver account isn't active yet</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Status: <b className="capitalize">{user.driver_verification_status?.replace(/_/g, " ")}</b>. You'll get jobs once our team verifies your account.
              </div>
            </div>
          </div>
        )}

        {/* Vehicle photo freshness banner — surfaces when on-file photos are missing
            or older than 90 days. Drivers re-capture front + back with live camera. */}
        {driverProfile && (driverProfile.vehicle_photos_expired || (driverProfile.vehicle_photos_days_remaining ?? 999) <= 14) && (
          <div
            className={`mb-4 rounded-md border p-4 flex items-start gap-3 ${
              driverProfile.vehicle_photos_expired
                ? "border-red-200 bg-red-50"
                : "border-amber-200 bg-amber-50"
            }`}
            data-testid="driver-vehicle-photos-banner"
          >
            <Truck size={20} weight="bold" className={`shrink-0 mt-0.5 ${driverProfile.vehicle_photos_expired ? "text-red-600" : "text-amber-700"}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${driverProfile.vehicle_photos_expired ? "text-red-900" : "text-amber-900"}`}>
                {driverProfile.vehicle_photos_expired
                  ? "Vehicle photos on file are out of date"
                  : `Vehicle photos expire in ${driverProfile.vehicle_photos_days_remaining} days`}
              </div>
              <div className={`text-xs mt-0.5 ${driverProfile.vehicle_photos_expired ? "text-red-800" : "text-amber-800"}`}>
                Take a fresh live photo of the front and back of your vehicle so ops always knows the on-file vehicle matches reality.
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => { setVehicleOpen(true); captureGeo(setVFrontGeo); captureGeo(setVBackGeo); }}
              className={driverProfile.vehicle_photos_expired ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
              data-testid="driver-update-vehicle-photos-btn"
            >
              <Camera size={13} weight="bold" className="mr-1.5" /> Update photos
            </Button>
          </div>
        )}

        {/* My Next Payout banner (shown across all tabs once we have earnings data) */}
        {earnings && (earnings.unpaid_balance > 0 || earnings.paid_out_total > 0) && (
          <div
            className={`mb-4 rounded-md border p-4 flex items-center justify-between gap-3 ${
              earnings.unpaid_balance > 0
                ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
                : "bg-zinc-50 border-zinc-200"
            }`}
            data-testid="driver-payout-banner"
          >
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                My next payout
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span
                  className={`text-xl font-semibold ${earnings.unpaid_balance > 0 ? "text-emerald-800" : "text-zinc-600"}`}
                  style={{ fontFamily: "Work Sans" }}
                  data-testid="driver-unpaid-balance"
                >
                  {earnings.currency} {Number(earnings.unpaid_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-[11px] text-zinc-600">
                  {earnings.unpaid_count} unpaid {earnings.unpaid_count === 1 ? "delivery" : "deliveries"}
                </span>
              </div>
              <div className="text-[11px] text-zinc-600 mt-1" data-testid="driver-next-payout-date">
                {earnings.unpaid_balance > 0 ? "Paid out " : "Next batch "}
                <b>{new Date(earnings.next_payout_date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</b>
              </div>
            </div>
            <Coins size={32} weight="duotone" className="text-emerald-500 shrink-0" />
          </div>
        )}

        {/* Driver leaderboard tile — quietly shows the driver where they stand
            in their fleet for the rolling 7-day window. */}
        <DriverLeaderboardTile data={leaderboard} />

        {/* Achievement badges — gamification tile with earned + locked badges
            and progress toward the next milestone. */}
        <DriverBadgesTile data={badges} />

        {/* Tab strip */}
        <div className="flex gap-2 mb-4" data-testid="driver-tabs">
          {[
            { v: "active", label: "Active", Icon: ListChecks },
            { v: "history", label: "History", Icon: ClockCounterClockwise },
            { v: "earnings", label: "Earnings", Icon: Coins },
          ].map((t) => {
            const active = tab === t.v;
            return (
              <button
                key={t.v}
                type="button"
                onClick={() => setTab(t.v)}
                data-testid={`driver-tab-${t.v}`}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold transition-all border ${
                  active
                    ? "bg-[#002BF6] text-white border-[#002BF6] shadow-sm"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <t.Icon size={14} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Earnings view */}
        {tab === "earnings" ? (
          loading || !earnings ? (
            <div className="text-center text-sm text-zinc-500 py-12">Loading...</div>
          ) : (
            <div data-testid="driver-earnings">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <EarningCard label="Lifetime earned" value={earnings.lifetime.driver_total} currency={earnings.lifetime.currency} subtitle={`${earnings.lifetime.deliveries} deliveries`} hero />
                <EarningCard label="This month" value={earnings.this_month.driver_total} currency={earnings.this_month.currency} subtitle={`${earnings.this_month.deliveries} this month`} />
                <EarningCard label="This week" value={earnings.this_week.driver_total} currency={earnings.this_week.currency} subtitle={`${earnings.this_week.deliveries} this week`} />
                <EarningCard label="ZipExpress cut (lifetime)" value={earnings.lifetime.zipexpress_total} currency={earnings.lifetime.currency} subtitle={`${earnings.lifetime.driver_share_pct ?? 86}% goes to you`} muted />
              </div>
              {earnings.jobs.length === 0 ? (
                <div className="bg-white border border-zinc-200 rounded-md p-10 text-center" data-testid="driver-earnings-empty">
                  <Coins size={36} className="mx-auto text-zinc-300 mb-2" />
                  <div className="text-sm font-semibold text-zinc-700">No earnings yet</div>
                  <div className="text-xs text-zinc-500 mt-1">Complete a ground delivery to start earning.</div>
                </div>
              ) : (
                <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
                  <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
                    <TrendUp size={14} weight="bold" className="text-emerald-600" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Per-job breakdown</span>
                  </div>
                  <div className="divide-y divide-zinc-100" data-testid="driver-earnings-jobs">
                    {earnings.jobs.map((j) => (
                      <div key={j.package_id} className="px-4 py-3" data-testid={`earning-${j.package_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <code className="text-[11px] font-mono bg-zinc-100 text-zinc-800 rounded px-1.5 py-0.5">{j.tracking_number}</code>
                            <div className="text-sm font-medium text-zinc-900 mt-1 truncate">{j.recipient_name || "—"}</div>
                            <div className="text-[11px] text-zinc-500">
                              {j.distance_km ? `${Number(j.distance_km).toFixed(2)} km` : "—"} ·{" "}
                              {new Date(j.earned_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-sm font-semibold text-emerald-700" data-testid={`earning-payout-${j.package_id}`}>
                              + {j.currency} {Number(j.driver_cut).toFixed(2)}
                            </div>
                            <div className="text-[10px] text-zinc-400">of {j.currency} {Number(j.total).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        ) : loading ? (
          <div className="text-center text-sm text-zinc-500 py-12">Loading...</div>
        ) : rows.length === 0 && pickups.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-md p-10 text-center" data-testid="driver-empty">
            <PackageIcon size={36} className="mx-auto text-zinc-300 mb-2" />
            <div className="text-sm font-semibold text-zinc-700">
              {tab === "active" ? "No active jobs right now" : "No completed jobs yet"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {tab === "active" ? "Check back soon — we'll auto-assign you the nearest ground package." : "Completed deliveries and pickups will show here."}
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="driver-jobs-list">
            {/* Pickup requests assigned to this driver — they need to GO collect the package */}
            {pickups.length > 0 && (
              <div className="space-y-2.5" data-testid="driver-pickups-list">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 px-1 pt-1 flex items-center gap-1.5">
                  <Storefront size={12} weight="bold" /> Pickups to collect ({pickups.length})
                </div>
                {pickups.map((p) => {
                  const mapsUrl = p.pickup_address
                    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                        p.pickup_lat && p.pickup_lng ? `${p.pickup_lat},${p.pickup_lng}` : p.pickup_address
                      )}&travelmode=driving`
                    : null;
                  return (
                    <div key={p.id} className="bg-white border border-sky-200 rounded-md p-4 sm:p-5" data-testid={`driver-pickup-${p.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-sky-50 text-sky-700 border-sky-200 inline-flex items-center gap-1">
                              <Storefront size={10} weight="fill" /> Pickup
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-zinc-100 text-zinc-700 border-zinc-200">
                              {p.status === "picked_up" ? "Collected" : "Scheduled"}
                            </span>
                            {p.signature_required && (
                              <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1" data-testid={`driver-pickup-sig-${p.id}`}>
                                <PenNib size={10} weight="fill" /> Signature
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-semibold text-zinc-900 truncate">{p.customer_name || "—"}</div>
                          {p.package_description && (
                            <div className="text-[11px] text-zinc-500 truncate">{p.package_description}</div>
                          )}
                        </div>
                        {p.weight_kg ? (
                          <div className="text-right shrink-0">
                            <div className="text-sm font-semibold text-zinc-900">{Number(p.weight_kg).toFixed(2)} kg</div>
                            <div className="text-[10px] text-zinc-500">{(Number(p.weight_kg) * KG_TO_LB).toFixed(2)} lb</div>
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-3 space-y-1.5 text-sm">
                        <div className="flex items-start gap-2 text-zinc-700" data-testid={`driver-pickup-addr-${p.id}`}>
                          <MapPin size={14} className="text-sky-500 mt-0.5 shrink-0" weight="bold" />
                          <span className="break-words"><b className="text-zinc-500 text-[11px] uppercase mr-1.5">Pickup:</b>{p.pickup_address}</span>
                        </div>
                        {p.destination_address && (
                          <div className="flex items-start gap-2 text-zinc-500 text-[12px]">
                            <NavigationArrow size={12} className="text-zinc-400 mt-0.5 shrink-0" weight="bold" />
                            <span className="break-words">
                              <b className="uppercase mr-1.5">To:</b>
                              {p.destination_name && (
                                <span className="text-zinc-700 font-semibold mr-1.5" data-testid={`driver-pickup-dest-name-${p.id}`}>
                                  {p.destination_name} ·
                                </span>
                              )}
                              {p.destination_address}
                            </span>
                          </div>
                        )}
                        {p.pickup_contact_phone && (
                          <a href={`tel:${p.pickup_contact_phone}`} className="flex items-center gap-2 text-[#002BF6] hover:underline" data-testid={`driver-pickup-tel-${p.id}`}>
                            <Phone size={13} weight="bold" />
                            <span>{p.pickup_contact_phone}</span>
                          </a>
                        )}
                        <div className="text-[11px] text-zinc-500">
                          {p.pickup_date || "ASAP"} · {p.pickup_time_window || "anytime"}
                        </div>
                      </div>

                      {tab === "active" && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {mapsUrl && (
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002BF6] hover:underline px-3 py-2 border border-[#002BF6]/30 rounded-md"
                              data-testid={`driver-pickup-nav-${p.id}`}
                            >
                              <NavigationArrow size={13} weight="bold" /> Navigate to pickup
                            </a>
                          )}
                          {p.status === "scheduled" && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => markPickupPickedUp(p)}
                              data-testid={`driver-pickup-collect-${p.id}`}
                            >
                              <CheckCircle size={13} weight="fill" className="mr-1.5" /> Mark picked up
                            </Button>
                          )}
                          {p.status === "picked_up" && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                              <CheckCircle size={13} weight="fill" /> Collected — awaiting conversion to delivery
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {rows.length > 0 && (
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700 px-1 pt-3 flex items-center gap-1.5">
                    <PackageIcon size={12} weight="bold" /> Deliveries ({rows.length})
                  </div>
                )}
              </div>
            )}
            {rows.map((p) => (
              <div key={p.id} className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid={`driver-job-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <code className="text-xs font-mono bg-zinc-100 text-zinc-800 rounded px-1.5 py-0.5">{p.tracking_number || p.id.slice(0, 8)}</code>
                      <MethodBadge method={p.shipment_method} />
                      <StatusPill status={p.status} />
                      {p.signature_required && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1" data-testid={`driver-sig-required-${p.id}`}>
                          <PenNib size={10} weight="fill" /> Signature
                        </span>
                      )}
                      {p.attempt_count > 0 && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border bg-orange-50 text-orange-800 border-orange-200" data-testid={`driver-attempts-${p.id}`}>
                          Attempt {p.attempt_count} of 2
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-zinc-900 truncate">
                      {p.recipient_name || "—"}
                    </div>
                  </div>
                  {p.weight_kg ? (
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-zinc-900">{Number(p.weight_kg).toFixed(2)} kg</div>
                      <div className="text-[10px] text-zinc-500">{(Number(p.weight_kg) * KG_TO_LB).toFixed(2)} lb</div>
                    </div>
                  ) : null}
                </div>

                <div className="mt-3 space-y-1.5 text-sm">
                  {p.sender_address && (
                    <div className="flex items-start gap-2 text-zinc-600" data-testid={`driver-sender-${p.id}`}>
                      <Storefront size={14} className="text-zinc-400 mt-0.5 shrink-0" weight="bold" />
                      <span className="break-words">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mr-1">Pickup:</span>
                        {p.sender_address}
                      </span>
                    </div>
                  )}
                  {p.recipient_address && (() => {
                    const navUrl = buildMapsNavUrl(p);
                    return navUrl ? (
                      <a
                        href={navUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-start gap-2 text-zinc-700 hover:text-[#002BF6] transition-colors group"
                        data-testid={`driver-address-nav-${p.id}`}
                        title="Open in Google Maps"
                      >
                        <MapPin size={14} className="text-zinc-400 group-hover:text-[#002BF6] mt-0.5 shrink-0" />
                        <span className="break-words underline-offset-2 group-hover:underline">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mr-1">Deliver to:</span>
                          {p.recipient_address}
                        </span>
                      </a>
                    ) : (
                      <div className="flex items-start gap-2 text-zinc-700">
                        <MapPin size={14} className="text-zinc-400 mt-0.5 shrink-0" />
                        <span className="break-words">{p.recipient_address}</span>
                      </div>
                    );
                  })()}
                  {p.recipient_phone && (
                    <a href={`tel:${p.recipient_phone}`} className="flex items-center gap-2 text-blue-600 hover:underline" data-testid={`driver-call-${p.id}`}>
                      <Phone size={14} className="text-zinc-400" />
                      <span>{p.recipient_phone}</span>
                    </a>
                  )}
                </div>

                {p.notes && (
                  <div className="mt-3 rounded bg-zinc-50 border border-zinc-100 px-3 py-2 text-xs text-zinc-600">
                    <span className="font-semibold text-zinc-700">Notes: </span>{p.notes}
                  </div>
                )}

                {tab === "active" && p.status !== "delivered" && p.status !== "cancelled" && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {buildMapsNavUrl(p, "inbound") && (
                      <button
                        type="button"
                        onClick={() => openMapsWithCurrentLocation(p, "inbound")}
                        data-testid={`driver-navigate-pickup-btn-${p.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-white border border-zinc-300 text-zinc-800 hover:border-[#002BF6] hover:text-[#002BF6] transition-colors"
                        title="Navigate to pickup origin"
                      >
                        <Storefront size={14} weight="bold" /> To pickup
                      </button>
                    )}
                    {buildMapsNavUrl(p, "outbound") && (
                      <button
                        type="button"
                        onClick={() => openMapsWithCurrentLocation(p, "outbound")}
                        data-testid={`driver-navigate-btn-${p.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-[#002BF6] text-white hover:bg-[#0021C7] transition-colors"
                        title="Navigate to recipient"
                      >
                        <NavigationArrow size={15} weight="fill" /> To recipient
                      </button>
                    )}
                    <Button
                      onClick={() => setDelivering(p)}
                      data-testid={`driver-deliver-btn-${p.id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Camera size={15} weight="bold" className="mr-1.5" /> Mark Delivered
                    </Button>
                    {p.attempt_count < 2 && (
                      <Button
                        type="button"
                        onClick={() => setAttempting(p)}
                        variant="outline"
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                        data-testid={`driver-attempt-btn-${p.id}`}
                        title="Mark recipient unavailable (counts as a delivery attempt)"
                      >
                        <UserMinus size={14} weight="bold" className="mr-1.5" /> Unavailable
                      </Button>
                    )}
                  </div>
                )}

                {p.pod_image_path && (
                  <div className="mt-3 text-[11px] text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle size={12} weight="fill" /> Proof of delivery uploaded
                  </div>
                )}
              </div>
            ))}
            <div className="text-center text-[11px] text-zinc-400 pt-2" data-testid="driver-jobs-count">
              {counts.shown} job{counts.shown === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </main>

      {/* Deliver dialog */}
      <Dialog open={!!delivering} onOpenChange={(v) => { if (!v) cancelPod(); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-md p-0 max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden" data-testid="deliver-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>Confirm delivery</DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 space-y-3 flex-1 overflow-y-auto min-h-0">
            {delivering && (
              <div className="rounded-md bg-zinc-50 border border-zinc-100 px-3 py-2 text-xs text-zinc-700">
                <div><b>{delivering.tracking_number}</b> → {delivering.recipient_name}</div>
                <div className="text-zinc-500 mt-0.5">{delivering.recipient_address}</div>
                {buildMapsNavUrl(delivering) && (
                  <button
                    type="button"
                    onClick={() => openMapsWithCurrentLocation(delivering, "outbound")}
                    className="inline-flex items-center gap-1 text-[11px] text-[#002BF6] hover:underline mt-1"
                    data-testid="deliver-dialog-navigate"
                  >
                    <NavigationArrow size={11} weight="fill" /> Open in Google Maps
                  </button>
                )}
              </div>
            )}
            <div className="text-sm text-zinc-700">
              Take a photo as <b>proof of delivery</b>. We'll email the recipient with their receipt.
            </div>

            {/* Module 2 — signature pad shown when the package requires it */}
            {delivering?.signature_required && !signatureDataUrl && !delivering?.signature_image_path && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="deliver-signature-required-block">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800 flex items-center gap-1.5 mb-2">
                  <PenNib size={12} weight="bold" /> Recipient signature required
                </div>
                {signing ? (
                  <SignaturePad
                    onSave={(dataUrl, name) => { setSignatureDataUrl(dataUrl); setSignedByName(name || ""); setSigning(false); }}
                    onCancel={() => setSigning(false)}
                    hint="Hand the phone to the recipient and ask them to sign"
                  />
                ) : (
                  <Button type="button" onClick={() => setSigning(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-signature-pad">
                    <PenNib size={14} weight="bold" className="mr-1.5" /> Capture signature
                  </Button>
                )}
              </div>
            )}
            {signatureDataUrl && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-2" data-testid="deliver-signature-captured">
                <div className="text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" /> Signature captured
                </div>
                <button type="button" onClick={() => { setSignatureDataUrl(null); setSigning(true); }} className="text-[11px] text-emerald-700 hover:underline" data-testid="deliver-signature-retake">
                  Retake
                </button>
              </div>
            )}

            {podPreview ? (
              <div className="relative">
                <img src={podPreview} alt="POD preview" className="w-full max-h-[40vh] object-contain rounded-md border border-zinc-200 bg-zinc-50" />
                <button
                  type="button"
                  onClick={cancelPod}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-zinc-200 flex items-center justify-center hover:bg-white"
                  data-testid="pod-clear-btn"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="pod-file-input"
                className="flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-zinc-200 rounded-md cursor-pointer hover:bg-zinc-50"
                data-testid="pod-file-trigger"
              >
                <Camera size={28} className="text-zinc-400" />
                <span className="text-sm font-medium text-zinc-700">Take or choose a photo</span>
                <span className="text-[11px] text-zinc-500">JPG / PNG / WEBP</span>
              </label>
            )}
            <input
              id="pod-file-input"
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPickFile}
              className="hidden"
              data-testid="pod-file-input"
            />
          </div>
          <DialogFooter className="px-5 py-4 border-t border-zinc-100 gap-2 sm:gap-2 shrink-0 bg-white">
            <Button variant="outline" onClick={cancelPod} data-testid="pod-cancel-btn">Cancel</Button>
            <Button
              onClick={submitPod}
              disabled={!podFile || uploading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="pod-submit-btn"
            >
              {uploading ? "Uploading..." : <><CheckCircle size={15} weight="bold" className="mr-1.5" /> Confirm delivery</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />

      {scanOpen && <BarcodeScanner onScan={handleScan} onClose={() => setScanOpen(false)} />}

      {/* Module 3 — record a failed delivery attempt */}
      <Dialog open={!!attempting} onOpenChange={(v) => { if (!v) { setAttempting(null); setAttemptNotes(""); } }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-md p-0" data-testid="attempt-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
            <DialogTitle>Record delivery attempt</DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 space-y-3">
            {attempting && (
              <div className="rounded-md bg-zinc-50 border border-zinc-100 px-3 py-2 text-xs text-zinc-700">
                <div><b>{attempting.tracking_number}</b> → {attempting.recipient_name}</div>
                <div className="text-zinc-500 mt-0.5">{attempting.recipient_address}</div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  This will be attempt <b>{(attempting.attempt_count || 0) + 1}</b> of 2.
                  {(attempting.attempt_count || 0) + 1 === 2 && (
                    <span className="text-orange-700 font-semibold"> The package will be flagged for return to sender if this attempt fails.</span>
                  )}
                </div>
              </div>
            )}
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Notes (optional)</label>
              <textarea
                value={attemptNotes}
                onChange={(e) => setAttemptNotes(e.target.value)}
                rows={3}
                className="mt-1.5 w-full text-sm rounded-md border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002BF6]"
                placeholder="e.g. No one at door, knocked twice, no answer"
                data-testid="attempt-notes"
              />
            </div>
            <div className="text-[11px] text-zinc-500">
              An automated email will be sent to the recipient letting them know delivery was attempted.
            </div>
          </div>
          <DialogFooter className="px-5 py-4 border-t border-zinc-100 gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setAttempting(null); setAttemptNotes(""); }} data-testid="attempt-cancel">Cancel</Button>
            <Button onClick={submitAttempt} disabled={attemptSubmitting} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="attempt-submit">
              {attemptSubmitting ? "Recording…" : (
                <><UserMinus size={14} weight="bold" className="mr-1.5" /> Recipient unavailable</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vehicle photo refresh dialog — drivers re-capture front + back with GPS */}
      <Dialog open={vehicleOpen} onOpenChange={(v) => { if (!v) setVehicleOpen(false); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] overflow-y-auto" data-testid="vehicle-photo-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100">
            <DialogTitle>Refresh vehicle photos</DialogTitle>
          </DialogHeader>
          <div className="px-5 py-4 space-y-4">
            <p className="text-xs text-zinc-600">
              Take a fresh live photo of the front <i>and</i> back of your vehicle. Photos are timestamped and tagged with your current GPS so courier ops can verify they're current.
            </p>
            <LiveCameraCapture
              label="Vehicle front (license plate visible)"
              helper="Stand directly in front of your vehicle, full plate visible."
              facing="environment"
              aspect="4/3"
              value={vFront}
              onChange={(v) => { setVFront(v); if (v) captureGeo(setVFrontGeo); }}
              testidPrefix="vehicle-front"
              icon={<Truck size={32} weight="bold" />}
            />
            <LiveCameraCapture
              label="Vehicle back"
              helper="Step around to the back and capture the full rear view."
              facing="environment"
              aspect="4/3"
              value={vBack}
              onChange={(v) => { setVBack(v); if (v) captureGeo(setVBackGeo); }}
              testidPrefix="vehicle-back"
              icon={<Truck size={32} weight="bold" />}
            />
          </div>
          <DialogFooter className="px-5 py-4 border-t border-zinc-100 gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setVehicleOpen(false)} data-testid="vehicle-photo-cancel">Cancel</Button>
            <Button
              onClick={submitVehiclePhotos}
              disabled={vSubmitting || !vFront || !vBack}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="vehicle-photo-submit"
            >
              {vSubmitting ? "Saving…" : (<><CheckCircle size={14} weight="bold" className="mr-1.5" /> Save photos</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Compact leaderboard pulse for the driver portal home. Shows the driver where
 * they stand within their fleet for the rolling 7-day window — public
 * recognition is one of the strongest non-monetary motivators in courier ops.
 *
 * Hidden when the driver doesn't yet have the minimum jobs to qualify, so we
 * never embarrass a brand-new driver with an empty card.
 */
function DriverLeaderboardTile({ data }) {
  if (!data || !data.my_rank) return null;
  const ordinal = (n) => {
    if (!n) return "—";
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const delta = data.week_over_week_delta_pct;
  const Arrow = delta == null || delta === 0 ? Minus : delta > 0 ? ArrowUp : ArrowDown;
  const arrowColor = delta == null || delta === 0 ? "text-zinc-400" : delta > 0 ? "text-emerald-600" : "text-red-500";
  // Visual treatment escalates with rank: gold for #1, silver for #2, bronze
  // for #3, neutral after that.
  const podiumColor =
    data.my_rank === 1 ? "from-amber-100 via-yellow-50 to-amber-100 border-amber-300"
    : data.my_rank === 2 ? "from-zinc-100 via-white to-zinc-100 border-zinc-300"
    : data.my_rank === 3 ? "from-orange-100 via-amber-50 to-orange-100 border-orange-300"
    : "from-blue-50 via-indigo-50 to-blue-50 border-blue-200";
  const trophyColor =
    data.my_rank === 1 ? "text-amber-500"
    : data.my_rank === 2 ? "text-zinc-500"
    : data.my_rank === 3 ? "text-orange-500"
    : "text-blue-500";
  return (
    <div
      className={`mb-4 rounded-md border bg-gradient-to-br p-4 flex items-center gap-4 ${podiumColor}`}
      data-testid="driver-leaderboard-tile"
    >
      <Trophy size={32} weight="duotone" className={`shrink-0 ${trophyColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-700">
          Your week
        </div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="text-xl font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }} data-testid="driver-leaderboard-rate">
            {data.my_success_rate_pct}%
          </span>
          <span className="text-xs text-zinc-600">
            success rate
          </span>
          <span className="text-zinc-300">·</span>
          <span className="text-sm font-semibold text-zinc-900" data-testid="driver-leaderboard-rank">
            ranked {ordinal(data.my_rank)}
          </span>
          <span className="text-xs text-zinc-600">
            of {data.fleet_size_qualified} in your fleet
          </span>
        </div>
        <div className="text-[11px] text-zinc-600 mt-1 flex items-center gap-1">
          <Arrow size={11} weight="bold" className={arrowColor} />
          {delta == null || delta === 0 ? (
            <span>{data.my_delivered}/{data.my_total} delivered this week</span>
          ) : (
            <span>
              <b className={arrowColor}>{delta > 0 ? "+" : ""}{delta} pts</b> vs last week ·
              {" "}{data.my_delivered}/{data.my_total} delivered
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Achievement badges tile for the Driver Portal home screen.
 *
 * Renders a horizontally-scrollable grid of earned badges in bright colour,
 * followed by a faded grid of locked badges with a small progress meter for
 * the few that have a measurable progression (counts toward a milestone,
 * current streak, etc). Designed to feel rewarding without being noisy: the
 * tile collapses to a single-line summary when there are no earned badges.
 */
const BADGE_ICON_MAP = {
  Package: PackageIcon,
  Trophy,
  Medal,
  Crown,
  Sparkle,
  Lightning,
  Rocket,
  Star,
  TrendUp,
  ShieldCheck,
};

function BadgeIcon({ name, size = 22, weight = "fill", className = "" }) {
  const Cmp = BADGE_ICON_MAP[name] || Trophy;
  return <Cmp size={size} weight={weight} className={className} />;
}

function DriverBadgesTile({ data }) {
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const earned = data.earned || [];
  const locked = data.locked || [];
  const next = data.next_milestone;
  const stats = data.stats || {};

  // Empty state — never seen earnings yet, surface a nudge instead.
  if (earned.length === 0 && (stats.total_delivered || 0) === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-zinc-300 bg-white p-4 mb-4 flex items-center gap-3"
        data-testid="driver-badges-tile-empty"
      >
        <Trophy size={24} weight="duotone" className="text-zinc-400 shrink-0" />
        <div className="text-xs text-zinc-600">
          Complete your first delivery to unlock your first achievement badge.
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-4 mb-4"
      data-testid="driver-badges-tile"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="driver-badges-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Trophy size={18} weight="fill" className="text-amber-500 shrink-0" />
          <span
            className="text-sm font-semibold text-zinc-900 uppercase tracking-wider"
            style={{ fontFamily: "Work Sans", letterSpacing: "0.06em" }}
          >
            Achievements
          </span>
          <span
            className="text-xs text-zinc-600"
            data-testid="driver-badges-count"
          >
            {earned.length} / {earned.length + locked.length} earned
          </span>
        </div>
        <span className="text-[11px] text-zinc-600">
          {open ? "Hide" : "View all"}
        </span>
      </button>

      {/* Earned strip (always visible when at least 1 earned) */}
      {earned.length > 0 && (
        <div
          className="mt-3 flex gap-2 overflow-x-auto pb-1"
          data-testid="driver-badges-earned-row"
        >
          {earned.map((b) => (
            <div
              key={b.id}
              title={b.desc}
              data-testid={`driver-badge-earned-${b.id}`}
              className="shrink-0 w-20 flex flex-col items-center gap-1 px-2 py-2 rounded-md bg-amber-100 border border-amber-300"
            >
              <BadgeIcon name={b.icon} className="text-amber-600" />
              <span
                className="text-[10px] font-semibold text-zinc-800 text-center leading-tight"
                style={{ fontFamily: "Work Sans" }}
              >
                {b.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Next milestone progress bar */}
      {next && (
        <div className="mt-3" data-testid="driver-badges-next-milestone">
          <div className="flex items-center justify-between text-[11px] text-zinc-700 mb-1">
            <span>
              Next: <b className="text-zinc-900">{next.label}</b>
            </span>
            <span data-testid="driver-badges-next-progress">
              {next.current} / {next.target}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.round((next.current / next.target) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Expanded view — locked badges with progress hints */}
      {open && locked.length > 0 && (
        <div
          className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
          data-testid="driver-badges-locked-grid"
        >
          {locked.map((b) => {
            const hasProgress =
              b.progress_current != null && b.progress_target != null;
            const pct = hasProgress
              ? Math.min(
                  100,
                  Math.round((b.progress_current / b.progress_target) * 100),
                )
              : 0;
            return (
              <div
                key={b.id}
                title={b.desc}
                data-testid={`driver-badge-locked-${b.id}`}
                className="rounded-md border border-zinc-200 bg-zinc-50 p-2 flex items-start gap-2"
              >
                <div className="relative shrink-0">
                  <BadgeIcon name={b.icon} className="text-zinc-300" />
                  <Lock
                    size={10}
                    weight="fill"
                    className="absolute -bottom-1 -right-1 text-zinc-400 bg-white rounded-full"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[11px] font-semibold text-zinc-700 leading-tight truncate"
                    style={{ fontFamily: "Work Sans" }}
                  >
                    {b.label}
                  </div>
                  <div className="text-[10px] text-zinc-500 leading-tight mt-0.5 line-clamp-2">
                    {b.desc}
                  </div>
                  {hasProgress && (
                    <div className="mt-1 h-1 rounded-full bg-zinc-200 overflow-hidden">
                      <div
                        className="h-full bg-zinc-400 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick stats footer */}
      {open && (
        <div
          className="mt-3 pt-3 border-t border-amber-100 grid grid-cols-3 gap-2 text-center"
          data-testid="driver-badges-stats"
        >
          <div>
            <div className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>
              {stats.total_delivered || 0}
            </div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider">
              Delivered
            </div>
          </div>
          <div>
            <div className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>
              {stats.current_streak || 0}
            </div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider">
              Streak
            </div>
          </div>
          <div>
            <div className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>
              {stats.last_7d_success_pct ?? 0}%
            </div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider">
              7-day rate
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
